<template>
  <div
      ref="rootElement"
      class="powerBalanceBlock-app">
    <div class="powerBalanceBlock-info">
      <div class="powerBalanceBlock-info-header">
        <div class="powerBalanceBlock-info-header-inner">
          <span class="powerBalanceBlock-eyebrow">{{ title }}</span>
          <div class="powerBalanceBlock-status"
               :class="`powerBalanceBlock-status--${statusLevel}`"
               role="status"
               aria-live="polite"
               aria-atomic="true">
            {{ statusText }}
          </div>
        </div>
        <p class="powerBalanceBlock-updated">{{ updatedText }}</p>
      </div>
      <a v-if="linkUrl"
         class="powerBalanceBlock-link"
         :href="linkUrl"
         :target="linkTarget || null"
         :title="linkTitle || null"
         :rel="linkRel || null">
        {{ linkText }}
        <span v-if="linkOpensInNewWindow" class="sr-only">({{ opensInNewWindowText }})</span>
      </a>
    </div>

    <div
        class="powerBalanceBlock-gauge"
        aria-hidden="true">
      <svg class="powerBalanceBlock-gauge-svg" viewBox="0 0 338 154" focusable="false">
        <defs>
          <clipPath :id="gaugeGradientClipId">
            <path d="M289.492 144.44C289.492 128.681 286.388 113.077 280.358 98.518C274.327 83.9589 265.488 70.7302 254.345 59.5872C243.202 48.4442 229.973 39.605 215.414 33.5745C200.855 27.5439 185.251 24.44 169.492 24.44C153.733 24.44 138.129 27.5439 123.57 33.5745C109.011 39.605 95.7822 48.4442 84.6392 59.5872C73.4962 70.7302 64.657 83.9589 58.6265 98.518C52.5959 113.077 49.492 128.681 49.492 144.44H73.492C73.492 131.833 75.9751 119.35 80.7996 107.702C85.624 96.0551 92.6953 85.4722 101.61 76.5578C110.524 67.6433 121.107 60.572 132.754 55.7476C144.402 50.9231 156.885 48.44 169.492 48.44C182.099 48.44 194.582 50.9231 206.23 55.7476C217.877 60.572 228.46 67.6433 237.374 76.5577C246.289 85.4722 253.36 96.0551 258.184 107.702C263.009 119.35 265.492 131.833 265.492 144.44H289.492Z"/>
          </clipPath>
        </defs>

        <path class="powerBalanceBlock-gauge-arc" d="M289.492 144.44C289.492 128.681 286.388 113.077 280.358 98.518C274.327 83.9589 265.488 70.7302 254.345 59.5872C243.202 48.4442 229.973 39.605 215.414 33.5745C200.855 27.5439 185.251 24.44 169.492 24.44C153.733 24.44 138.129 27.5439 123.57 33.5745C109.011 39.605 95.7822 48.4442 84.6392 59.5872C73.4962 70.7302 64.657 83.9589 58.6265 98.518C52.5959 113.077 49.492 128.681 49.492 144.44H73.492C73.492 131.833 75.9751 119.35 80.7996 107.702C85.624 96.0551 92.6953 85.4722 101.61 76.5578C110.524 67.6433 121.107 60.572 132.754 55.7476C144.402 50.9231 156.885 48.44 169.492 48.44C182.099 48.44 194.582 50.9231 206.23 55.7476C217.877 60.572 228.46 67.6433 237.374 76.5577C246.289 85.4722 253.36 96.0551 258.184 107.702C263.009 119.35 265.492 131.833 265.492 144.44H289.492Z"/>
        <g :clip-path="`url(#${gaugeGradientClipId})`">
          <g transform="matrix(0 0.0508 -0.0772556 0 169.492 84.44)">
            <foreignObject x="-1566.23" y="-1566.23" width="3132.46" height="3132.46">
              <div xmlns="http://www.w3.org/1999/xhtml" class="powerBalanceBlock-gauge-gradient"></div>
            </foreignObject>
          </g>
        </g>

        <path class="powerBalanceBlock-gauge-label-path" d="M164.208 11.68C161.984 11.68 160.48 10.496 160.064 8.48L161.36 8.16C161.68 9.68 162.576 10.432 164.208 10.432C165.856 10.432 166.976 9.36 166.976 7.696C166.976 6.144 165.904 4.992 164.208 4.992C163.072 4.992 162.224 5.456 161.52 6.176L160.352 5.888L161.424 0.240002H167.296V1.488H162.416L161.808 4.592L161.84 4.608C162.528 4.112 163.344 3.84 164.416 3.84C166.672 3.84 168.304 5.472 168.304 7.696C168.304 10.064 166.624 11.68 164.208 11.68ZM174.598 11.68C172.006 11.68 170.166 9.472 170.166 5.84C170.166 2.208 171.974 1.90735e-06 174.598 1.90735e-06C177.222 1.90735e-06 179.03 2.208 179.03 5.84C179.03 9.472 177.222 11.68 174.598 11.68ZM174.598 10.432C176.47 10.432 177.702 8.768 177.702 5.84C177.702 2.912 176.47 1.248 174.598 1.248C172.726 1.248 171.494 2.912 171.494 5.84C171.494 8.768 172.726 10.432 174.598 10.432Z"/>
        <path class="powerBalanceBlock-gauge-label-path" d="M6.928 144.44H5.6V142.152H4.41074e-06V141L4.928 133.24H6.928V140.984H8.368V142.152H6.928V144.44ZM5.568 134.28L1.328 140.984H5.6V134.28H5.568ZM14.5503 133C17.0783 133 18.7423 134.728 18.7423 138.168C18.7423 142.712 17.5263 144.68 14.6623 144.68C12.8703 144.68 11.5263 143.64 11.0623 141.832L12.2783 141.512C12.6943 142.904 13.5423 143.496 14.6623 143.496C16.4383 143.496 17.2703 142.328 17.3983 139.368H17.3663C16.7903 140.152 15.7983 140.632 14.4863 140.632C12.0703 140.632 10.5823 139.16 10.5823 136.792C10.5823 134.584 12.2623 133 14.5503 133ZM14.5503 139.464C16.1343 139.464 17.2063 138.424 17.2063 136.872C17.2063 135.272 16.1503 134.184 14.5503 134.184C12.9343 134.184 11.8783 135.24 11.8783 136.792C11.8783 138.44 12.9183 139.464 14.5503 139.464ZM23.1645 144.44H21.2605V142.584H23.1645V144.44ZM29.833 144.68C27.321 144.68 25.689 143.336 25.689 141.32C25.689 139.912 26.473 138.84 27.785 138.312V138.28C26.793 137.752 26.313 136.984 26.313 135.944C26.313 134.248 27.785 133 29.833 133C31.865 133 33.353 134.248 33.353 135.944C33.353 136.968 32.873 137.736 31.881 138.28V138.312C33.177 138.84 33.961 139.912 33.961 141.32C33.961 143.336 32.329 144.68 29.833 144.68ZM29.833 137.816C31.145 137.816 32.041 137.096 32.041 136.024C32.041 134.936 31.145 134.168 29.833 134.168C28.505 134.168 27.625 134.936 27.625 136.024C27.625 137.096 28.505 137.816 29.833 137.816ZM29.833 143.4C31.529 143.4 32.601 142.568 32.601 141.24C32.601 139.896 31.529 139 29.833 139C28.121 139 27.049 139.896 27.049 141.24C27.049 142.568 28.121 143.4 29.833 143.4Z"/>
        <path class="powerBalanceBlock-gauge-label-path" d="M26.928 74.4399H25.6V72.1519H20V70.9999L24.928 63.2399H26.928V70.9839H28.368V72.1519H26.928V74.4399ZM25.568 64.2799L21.328 70.9839H25.6V64.2799H25.568ZM34.5503 62.9999C37.0783 62.9999 38.7423 64.7279 38.7423 68.1679C38.7423 72.7119 37.5263 74.6799 34.6623 74.6799C32.8703 74.6799 31.5263 73.6399 31.0623 71.8319L32.2783 71.5119C32.6943 72.9039 33.5423 73.4959 34.6623 73.4959C36.4383 73.4959 37.2703 72.3279 37.3983 69.3679H37.3663C36.7903 70.1519 35.7983 70.6319 34.4863 70.6319C32.0703 70.6319 30.5823 69.1599 30.5823 66.7919C30.5823 64.5839 32.2623 62.9999 34.5503 62.9999ZM34.5503 69.4639C36.1343 69.4639 37.2063 68.4239 37.2063 66.8719C37.2063 65.2719 36.1503 64.1839 34.5503 64.1839C32.9343 64.1839 31.8783 65.2399 31.8783 66.7919C31.8783 68.4399 32.9183 69.4639 34.5503 69.4639ZM43.1645 74.4399H41.2605V72.5839H43.1645V74.4399ZM49.769 62.9999C52.297 62.9999 53.961 64.7279 53.961 68.1679C53.961 72.7119 52.745 74.6799 49.881 74.6799C48.089 74.6799 46.745 73.6399 46.281 71.8319L47.497 71.5119C47.913 72.9039 48.761 73.4959 49.881 73.4959C51.657 73.4959 52.489 72.3279 52.617 69.3679H52.585C52.009 70.1519 51.017 70.6319 49.705 70.6319C47.289 70.6319 45.801 69.1599 45.801 66.7919C45.801 64.5839 47.481 62.9999 49.769 62.9999ZM49.769 69.4639C51.353 69.4639 52.425 68.4239 52.425 66.8719C52.425 65.2719 51.369 64.1839 49.769 64.1839C48.153 64.1839 47.097 65.2399 47.097 66.7919C47.097 68.4399 48.137 69.4639 49.769 69.4639Z"/>
        <path class="powerBalanceBlock-gauge-label-path" d="M308.208 144.68C305.984 144.68 304.48 143.496 304.064 141.48L305.36 141.16C305.68 142.68 306.576 143.432 308.208 143.432C309.856 143.432 310.976 142.36 310.976 140.696C310.976 139.144 309.904 137.992 308.208 137.992C307.072 137.992 306.224 138.456 305.52 139.176L304.352 138.888L305.424 133.24H311.296V134.488H306.416L305.808 137.592L305.84 137.608C306.528 137.112 307.344 136.84 308.416 136.84C310.672 136.84 312.304 138.472 312.304 140.696C312.304 143.064 310.624 144.68 308.208 144.68ZM318.598 144.68C316.006 144.68 314.166 142.472 314.166 138.84C314.166 135.208 315.974 133 318.598 133C321.222 133 323.03 135.208 323.03 138.84C323.03 142.472 321.222 144.68 318.598 144.68ZM318.598 143.432C320.47 143.432 321.702 141.768 321.702 138.84C321.702 135.912 320.47 134.248 318.598 134.248C316.726 134.248 315.494 135.912 315.494 138.84C315.494 141.768 316.726 143.432 318.598 143.432ZM326.524 144.44H324.62V142.584H326.524V144.44ZM331.112 141.816C330.952 142.136 330.872 142.52 330.856 143.192H336.888V144.44H329.448V143.384C329.448 142.472 329.592 141.784 329.912 141.256C330.312 140.52 331 139.976 332.744 139.064C335.08 137.864 335.752 137.192 335.752 136.072C335.752 135 334.776 134.248 333.416 134.248C331.72 134.248 330.728 135.224 330.6 136.968L329.288 136.856C329.416 134.488 331 133 333.416 133C335.48 133 337.064 134.344 337.064 136.072C337.064 137.72 336.232 138.696 333.768 139.944C331.912 140.888 331.384 141.256 331.112 141.816Z"/>
        <path class="powerBalanceBlock-gauge-label-path" d="M288.208 74.6799C285.984 74.6799 284.48 73.4959 284.064 71.4799L285.36 71.1599C285.68 72.6799 286.576 73.4319 288.208 73.4319C289.856 73.4319 290.976 72.3599 290.976 70.6959C290.976 69.1439 289.904 67.9919 288.208 67.9919C287.072 67.9919 286.224 68.4559 285.52 69.1759L284.352 68.8879L285.424 63.2399H291.296V64.4879H286.416L285.808 67.5919L285.84 67.6079C286.528 67.1119 287.344 66.8399 288.416 66.8399C290.672 66.8399 292.304 68.4719 292.304 70.6959C292.304 73.0639 290.624 74.6799 288.208 74.6799ZM298.598 74.6799C296.006 74.6799 294.166 72.4719 294.166 68.8399C294.166 65.2079 295.974 62.9999 298.598 62.9999C301.222 62.9999 303.03 65.2079 303.03 68.8399C303.03 72.4719 301.222 74.6799 298.598 74.6799ZM298.598 73.4319C300.47 73.4319 301.702 71.7679 301.702 68.8399C301.702 65.9119 300.47 64.2479 298.598 64.2479C296.726 64.2479 295.494 65.9119 295.494 68.8399C295.494 71.7679 296.726 73.4319 298.598 73.4319ZM306.524 74.4399H304.62V72.5839H306.524V74.4399ZM313.928 63.2399V73.1919H316.344V74.4399H309.992V73.1919H312.6V64.6959L310.104 66.3119V64.9679L312.76 63.2399H313.928Z"/>

        <g class="powerBalanceBlock-gauge-ticks">
          <line x1="169.628" y1="23.0764" x2="169.628" y2="49.8036"/>
          <line x1="92.4208" y1="89.7152" x2="69.2743" y2="76.3515"/>
          <line x1="1.63636" y1="-1.63636" x2="28.3636" y2="-1.63636" transform="matrix(0.866025 -0.5 -0.5 -0.866025 244.992 89.1162)"/>
          <line x1="74.3556" y1="144.076" x2="47.6284" y2="144.076"/>
          <line x1="291.356" y1="144.076" x2="264.628" y2="144.076"/>
        </g>

        <g :transform="`rotate(${needleDegrees} 169.992 144.44)`">
          <g
              class="powerBalanceBlock-needle"
              :class="{ 'powerBalanceBlock-needle--trembling': isTrembling }">
            <path d="M169.992 61.4399L179.686 142.518C180.381 148.328 175.844 153.44 169.992 153.44C164.14 153.44 159.603 148.328 160.298 142.518L169.992 61.4399Z"/>
          </g>
        </g>
      </svg>
    </div>

    <div class="powerBalanceBlock-frequency">
      <span class="powerBalanceBlock-eyebrow-small">{{ frequencyTitle }}</span>
      <span class="powerBalanceBlock-frequency-value"><span class="powerBalanceBlock-frequency-value-number">{{displayValue || '–' }}</span> <span class="powerBalanceBlock-frequency-unit">Hz</span></span>
      <p v-if="hasFetchError" class="powerBalanceBlock-error" role="alert">
        {{ fetchErrorText }}
      </p>
    </div>

    <a v-if="linkUrl"
       class="powerBalanceBlock-link powerBalanceBlock-link--mobile"
       :href="linkUrl"
       :target="linkTarget || null"
       :title="linkTitle || null"
       :rel="linkRel || null">
      {{ linkText }}
      <span v-if="linkOpensInNewWindow" class="sr-only">({{ opensInNewWindowText }})</span>
    </a>
  </div>
</template>

<script
    setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch
} from 'vue';
import {
  LEVELS,
  getLevel
} from '../constants/Gauge.constants.js';
import {
  fetchFrequency
} from '../service.js';

const STANDARD_HZ = 50;
const FETCH_INTERVAL = 1000 * 10;
const MIN_FETCH_INTERVAL = 1000 * 10;
const STARTUP_ANIMATION_DURATION = 1800;
const UPDATE_ANIMATION_DURATION = 650;
const GAUGE_SCALE_POINTS = [
  {
    value: 49.8,
    degrees: -90,
  },
  {
    value: 49.9,
    degrees: -55,
  },
  {
    value: 50,
    degrees: 0,
  },
  {
    value: 50.1,
    degrees: 55,
  },
  {
    value: 50.2,
    degrees: 90,
  },
];

const props = defineProps({
  instanceId: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: 'Kraftbalansen',
  },
  linkUrl: {
    type: String,
    default: '',
  },
  linkText: {
    type: String,
    default: '',
  },
  linkTarget: {
    type: String,
    default: '',
  },
  linkTitle: {
    type: String,
    default: '',
  },
  linkRel: {
    type: String,
    default: '',
  },
  linkOpensInNewWindow: {
    type: Boolean,
    default: false,
  },
  opensInNewWindowText: {
    type: String,
    default: '',
  },
  frequencyTitle: {
    type: String,
    default: 'Systemfrekvens',
  },
  greenStatusText: {
    type: String,
    default: '',
  },
  yellowStatusText: {
    type: String,
    default: '',
  },
  redStatusText: {
    type: String,
    default: '',
  },
  fetchErrorText: {
    type: String,
    default: '',
  },
  fetchInterval: {
    type: [Number, String],
    default: null,
  },
  enableStartupAnimation: {
    type: Boolean,
    default: false,
  },
  lang: {
    type: String,
    default: 'no',
  },
});

const gaugeGradientClipId = `${props.instanceId}-gauge-gradient-clip`;
const rootElement = ref(null);
const interval = ref(null);
const observer = ref(null);
const animationFrame = ref(null);
const frequency = ref(null);
const updatedAt = ref(null);
const hasFetchError = ref(false);
const isTrembling = ref(false);
const hasEnteredViewport = ref(false);
const hasPlayedStartupAnimation = ref(false);
const isStartupAnimating = ref(false);
const isFrequencyUpdating = ref(false);
const startupProgress = ref(0);
const hasReceivedFrequency = ref(false);
const presentedFrequency = ref(null);

const numericFrequency = computed(() => frequency.value == null ? NaN : Number(frequency.value));

const shouldUseStartupValue = computed(() => {
  return props.enableStartupAnimation && !hasPlayedStartupAnimation.value;
});

const needleDegrees = computed(() => {
  return getGaugeDegrees(getVisibleGaugeFrequency());
});

const displayValue = computed(() => {
  if (shouldUseStartupValue.value) {
    return formatFrequency(getStartupDisplayFrequency());
  }

  if (!isValid.value && !Number.isFinite(presentedFrequency.value)) {
    return '';
  }

  return formatFrequency(getVisibleDisplayFrequency());
});

const isValid = computed(() => {
  return Number.isFinite(numericFrequency.value);
});

const statusLevel = computed(() => {
  return getFrequencyStatusLevel(getStatusFrequency()).toLowerCase();
});

const statusText = computed(() => {
  const level = statusLevel.value;

  if (['normal', LEVELS.GREEN.toLowerCase()].includes(level)) {
    return getFallbackText(props.greenStatusText, 'Stabil', 'Stable');
  }

  if (['pressed', 'strained', LEVELS.YELLOW.toLowerCase()].includes(level)) {
    return getFallbackText(props.yellowStatusText, 'Ustabil', 'Unstable');
  }

  if (['severe', 'rationed', LEVELS.RED.toLowerCase()].includes(level)) {
    return getFallbackText(props.redStatusText, 'Kritisk', 'Critical');
  }

  return isEnglish.value ? 'Unavailable' : 'Utilgjengelig';
});

const fetchErrorText = computed(() => {
  return getFallbackText(props.fetchErrorText, 'Kunne ikke hente oppdaterte data.', 'Could not fetch updated data.');
});

const updatedText = computed(() => {
  if (!updatedAt.value) {
    return isEnglish.value ? 'Updates continuously' : 'Oppdateres fortløpende';
  }

  return `${isEnglish.value ? 'Updated at' : 'Oppdatert kl.'} ${updatedAt.value.toLocaleTimeString(isEnglish.value ? 'en-GB' : 'nb-NO', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
});

const isEnglish = computed(() => {
  return props.lang && props.lang.toLowerCase().startsWith('en');
});

async function fetchData() {
  try {
    hasFetchError.value = false;

    const previousPresentedFrequency = getCurrentPresentedFrequency();
    const shouldAnimateUpdate = hasReceivedFrequency.value
        && hasPlayedStartupAnimation.value
        && !isStartupAnimating.value
        && !prefersReducedMotion();

    const nextFrequency = await fetchFrequency();
    const nextNumericFrequency = Number(nextFrequency);

    frequency.value = nextFrequency;
    updatedAt.value = new Date();

    if (!Number.isFinite(nextNumericFrequency)) {
      return;
    }

    if (shouldAnimateUpdate && Number.isFinite(previousPresentedFrequency) && previousPresentedFrequency !== nextNumericFrequency) {
      runFrequencyUpdateAnimation(previousPresentedFrequency, nextNumericFrequency);
    } else {
      presentedFrequency.value = nextNumericFrequency;
    }

    hasReceivedFrequency.value = true;
  } catch (error) {
    hasFetchError.value = true;
    frequency.value = null;

    if (error instanceof RangeError) {
      resetPresentedFrequency();
    }

    console.error(error);
  }
}

onMounted(() => {
  setupStartupAnimationObserver();
  fetchData();

  const nextFetchInterval = Number(props.fetchInterval || FETCH_INTERVAL);

  if (nextFetchInterval < MIN_FETCH_INTERVAL) {
    console.error('Fetch interval is too low:', nextFetchInterval);
    return;
  }

  interval.value = setInterval(fetchData, nextFetchInterval);
});

watch([isValid, isStartupAnimating, isFrequencyUpdating], () => {
  if (isValid.value && !isStartupAnimating.value && !isFrequencyUpdating.value) {
    startTrembling();
    return;
  }

  stopTrembling();
}, {
  immediate: true,
});

onUnmounted(() => {
  clearInterval(interval.value);
  observer.value?.disconnect();
  cancelAnimationFrame(animationFrame.value);
  stopTrembling();
});

watch([isValid, hasEnteredViewport], () => {
  startStartupAnimationWhenReady();
});

function setupStartupAnimationObserver() {
  if (!props.enableStartupAnimation || prefersReducedMotion()) {
    hasEnteredViewport.value = true;
    hasPlayedStartupAnimation.value = true;
    startupProgress.value = 1;
    return;
  }

  if (!('IntersectionObserver' in window)) {
    hasEnteredViewport.value = true;
    return;
  }

  observer.value = new IntersectionObserver((entries) => {
    if (!entries.some(entry => entry.isIntersecting)) {
      return;
    }

    hasEnteredViewport.value = true;
    observer.value?.disconnect();
    observer.value = null;
  }, {
    threshold: 0.35,
  });

  if (rootElement.value) {
    observer.value.observe(rootElement.value);
  }
}

function startStartupAnimationWhenReady() {
  if (!props.enableStartupAnimation || prefersReducedMotion() || hasPlayedStartupAnimation.value || isStartupAnimating.value || !hasEnteredViewport.value || !isValid.value) {
    return;
  }

  runStartupAnimation();
}

function runStartupAnimation() {
  const startTime = performance.now();

  isStartupAnimating.value = true;
  stopTrembling();
  startupProgress.value = 0;

  function frame(now) {
    const progress = Math.min((now - startTime) / STARTUP_ANIMATION_DURATION, 1);

    startupProgress.value = easeInOutCubic(progress);

    if (progress < 1) {
      animationFrame.value = requestAnimationFrame(frame);
      return;
    }

    startupProgress.value = 1;
    hasPlayedStartupAnimation.value = true;
    isStartupAnimating.value = false;
    startTrembling();
  }

  animationFrame.value = requestAnimationFrame(frame);
}

function runFrequencyUpdateAnimation(fromFrequency, toFrequency) {
  cancelAnimationFrame(animationFrame.value);
  stopTrembling();

  const startTime = performance.now();

  isFrequencyUpdating.value = true;
  presentedFrequency.value = fromFrequency;

  function frame(now) {
    const progress = Math.min((now - startTime) / UPDATE_ANIMATION_DURATION, 1);
    const easedProgress = easeInOutCubic(progress);

    presentedFrequency.value = scale(easedProgress, [0, 1], [fromFrequency, toFrequency]);

    if (progress < 1) {
      animationFrame.value = requestAnimationFrame(frame);
      return;
    }

    presentedFrequency.value = toFrequency;
    isFrequencyUpdating.value = false;
    startTrembling();
  }

  animationFrame.value = requestAnimationFrame(frame);
}

function startTrembling() {
  if (prefersReducedMotion()) {
    stopTrembling();
    return;
  }

  isTrembling.value = true;
}

function stopTrembling() {
  isTrembling.value = false;
}

function resetPresentedFrequency() {
  cancelAnimationFrame(animationFrame.value);
  presentedFrequency.value = null;
  hasReceivedFrequency.value = false;
  isFrequencyUpdating.value = false;
  isStartupAnimating.value = false;
  startupProgress.value = 0;
  stopTrembling();
}

function getFallbackText(value, norwegianFallback, englishFallback) {
  return value || (isEnglish.value ? englishFallback : norwegianFallback);
}

function formatFrequency(value) {
  const number = Number(value);

  return Number.isFinite(number)
      ? number.toFixed(2).replace('.', ',')
      : '0,00';
}

function getVisibleDisplayFrequency() {
  if (shouldUseStartupValue.value) {
    return getStartupDisplayFrequency();
  }

  return Number.isFinite(presentedFrequency.value)
      ? presentedFrequency.value
      : numericFrequency.value;
}

function getVisibleGaugeFrequency() {
  if (shouldUseStartupValue.value) {
    return getStartupGaugeFrequency();
  }

  return Number.isFinite(presentedFrequency.value)
      ? presentedFrequency.value
      : numericFrequency.value;
}

function getStartupDisplayFrequency() {
  return isValid.value
      ? numericFrequency.value * startupProgress.value
      : 0;
}

function getStartupGaugeFrequency() {
  const firstPoint = GAUGE_SCALE_POINTS[0];
  const targetFrequency = isValid.value
      ? numericFrequency.value
      : firstPoint.value;

  return scale(startupProgress.value, [0, 1], [firstPoint.value, targetFrequency]);
}

function getCurrentPresentedFrequency() {
  if (shouldUseStartupValue.value) {
    return getStartupDisplayFrequency();
  }

  if (Number.isFinite(presentedFrequency.value)) {
    return presentedFrequency.value;
  }

  return numericFrequency.value;
}

function getStatusFrequency() {
  if (isValid.value) {
    return numericFrequency.value;
  }

  if (hasReceivedFrequency.value && Number.isFinite(presentedFrequency.value)) {
    return presentedFrequency.value;
  }

  return NaN;
}

function easeInOutCubic(value) {
  return value < 0.5
      ? 4 * value * value * value
      : 1 - Math.pow(-2 * value + 2, 3) / 2;
}

function getFrequencyStatusLevel(value) {
  const frequencyValue = Number(value);

  if (!Number.isFinite(frequencyValue)) {
    return 'Unknown';
  }

  return getLevel(frequencyValue) || LEVELS.RED;
}

function getGaugeDegrees(value) {
  const firstPoint = GAUGE_SCALE_POINTS[0];
  const lastPoint = GAUGE_SCALE_POINTS[GAUGE_SCALE_POINTS.length - 1];

  if (!Number.isFinite(Number(value))) {
    return firstPoint.degrees;
  }

  if (value <= firstPoint.value) {
    return firstPoint.degrees;
  }

  if (value >= lastPoint.value) {
    return lastPoint.degrees;
  }

  for (let index = 1; index < GAUGE_SCALE_POINTS.length; index++) {
    const previousPoint = GAUGE_SCALE_POINTS[index - 1];
    const nextPoint = GAUGE_SCALE_POINTS[index];

    if (value <= nextPoint.value) {
      return scale(value, [previousPoint.value, nextPoint.value], [previousPoint.degrees, nextPoint.degrees]);
    }
  }

  return lastPoint.degrees;
}

function scale(number, [inMin, inMax], [outMin, outMax]) {
  return (number - inMin) / (inMax - inMin) * (outMax - outMin) + outMin;
}

function prefersReducedMotion() {
  return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
</script>
